import { memo, useState, type Dispatch, type SetStateAction } from "react";
import {
	useDraggable,
	type DraggableAttributes,
	type DraggableSyntheticListeners,
} from "@dnd-kit/core";
import {
	Calendar,
	Clock,
	GripVertical,
	ListChecks,
	Trash2,
	CheckCircle2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { AvatarStack } from "@/components/ui/avatar-stack";
import { Icon, type IconName } from "@/components/ui/icon-picker";
import { GoogleCalendarIcon } from "@/components/icons/GoogleCalendarIcon";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn, formatDate } from "@/lib/utils";
import { type Project } from "@/services/project.service";
import {
	type UiTask,
	type ColumnId,
	COLUMN_IDS,
	PRIORITY_BORDER,
	allAssignees,
	formatTime,
} from "./types";
import { PrBadge } from "./github/PrBadge";

// ── TaskCardContent ───────────────────────────────────────────────────────────

function TaskCardContentBase({
	task,
	projectsById,
	isDragging = false,
}: {
	task: UiTask;
	projectsById: Map<string, Project>;
	isDragging?: boolean;
}) {
	const projectName = projectsById.get(task.project_id)?.name ?? "—";
	const assignees = allAssignees(task);
	const checklistTotal = task.checklist_total ?? 0;
	const checklistDone = task.checklist_done ?? 0;
	const checklistPct =
		checklistTotal > 0
			? Math.round((checklistDone / checklistTotal) * 100)
			: 0;

	return (
		<div
			className={cn(
				"bg-surface rounded-lg border border-border p-3 transition-all flex flex-col gap-2.5 select-none",
				PRIORITY_BORDER[task.priority],
				isDragging
					? "shadow-xl opacity-90 rotate-1"
					: "shadow-xs hover:shadow-md",
			)}
		>
			<div className="flex items-start justify-between gap-2">
				<div className="flex items-center gap-1.5 flex-wrap">
					{task.task_type && (
						<span
							className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[12px] font-medium leading-none absolute right-4 transition-all duration-200 group-hover:right-8 top-4"
							style={{
								// background: `${task.task_type.color}1a`,
								color: task.task_type.color,
							}}
							title={task.task_type.name}
						>
							<Icon
								name={task.task_type.icon as IconName}
								className="h-3 w-3"
							/>
							{task.task_type.name}
						</span>
					)}
					<Badge variant={task.priority} className="text-[10px]">
						{task.priority.charAt(0).toUpperCase() +
							task.priority.slice(1)}
					</Badge>
					{task.ticket_code && (
						<span className="text-[10px] bg-muted-subtle text-muted-foreground px-1.5 py-0.5 rounded font-medium leading-none font-mono">
							#{task.ticket_code}
						</span>
					)}
					{task.github && (
						<PrBadge
							state={task.github.pr_state}
							prNumber={task.github.pr_number}
						/>
					)}
				</div>
			</div>

			<p className="text-[10px] text-muted -mb-2">{projectName}&nbsp;</p>
			<p className="text-sm mb-0 font-medium text-foreground leading-snug">
				{task.title}
			</p>

			{task.tags.length > 0 && (
				<div className="flex items-center gap-1 flex-wrap -mt-1">
					{task.tags.map((tag) => (
						<span
							key={tag}
							className="text-[10px] bg-muted-subtle text-muted-foreground px-1.5 py-0.5 rounded font-medium leading-none"
						>
							{tag}
						</span>
					))}
				</div>
			)}

			{checklistTotal > 0 && (
				<div className="flex items-center gap-1.5 mb-1">
					<ListChecks className="h-3 w-3 text-muted shrink-0" />
					<div className="h-1 flex-1 rounded-full bg-muted-subtle overflow-hidden">
						<div
							className="h-full bg-primary transition-all"
							style={{ width: `${checklistPct}%` }}
						/>
					</div>
					<span className="text-[11px] text-muted leading-none shrink-0">
						{checklistDone}/{checklistTotal}
					</span>
				</div>
			)}

			<div className="flex items-center justify-between pt-2 -mb-0.5 border-t border-border">
				<div className="flex items-center gap-1.5">
					{assignees.length > 0 ? (
						<AvatarStack profiles={assignees} size="sm" />
					) : (
						<span />
					)}
				</div>
				<div className="flex items-center gap-2">
					{task.has_calendar_event ? (
						<GoogleCalendarIcon
							className="h-4 w-4"
							title="On your Google Calendar"
						/>
					) : (
						<GoogleCalendarIcon
							className="h-4 w-4 grayscale opacity-40"
							title="On your Google Calendar"
						/>
					)}
					{task.sprint && (
						<p className="text-[10px] text-muted">
							<>
								{task.sprint.name}
								{(task.active_sprint_estimated_time ?? 0) > 0 ||
								task.due_date ? (
									<>
										&nbsp;
										<span className="font-bold">
											&middot;
										</span>
									</>
								) : (
									""
								)}
							</>
						</p>
					)}
					{(task.active_sprint_estimated_time ?? 0) > 0 && (
						<div className="flex items-center gap-1 text-[11px] text-muted">
							<Clock className="h-3 w-3" />
							{formatTime(task.active_sprint_estimated_time ?? 0)}
						</div>
					)}
					{task.due_date && (
						<div className="flex items-center gap-1 text-[11px] text-muted">
							<Calendar className="h-3 w-3" />
							{task.due_date
								? formatDate(task.due_date.slice(0, 10))
								: "—"}
						</div>
					)}
				</div>
			</div>
		</div>
	);
}

export const TaskCardContent = memo(TaskCardContentBase);

// ── CardActions ───────────────────────────────────────────────────────────────

// Hover-only grip + delete controls, memoized apart from the draggable wrapper.
// dnd-kit re-renders every draggable whenever the drop target changes; keeping
// the Radix tooltips here stops those re-renders from rebuilding them per card.
const CardActions = memo(function CardActions({
	task,
	confirming,
	setConfirming,
	onDelete,
	attributes,
	listeners,
}: {
	task: UiTask;
	confirming: boolean;
	setConfirming: Dispatch<SetStateAction<boolean>>;
	onDelete: (task: UiTask) => void;
	attributes: DraggableAttributes;
	listeners: DraggableSyntheticListeners;
}) {
	return (
		<>
			<div
				{...attributes}
				{...listeners}
				className="absolute top-4 bg-white dark:bg-black right-2 z-10 p-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing text-muted hover:text-primary"
				onClick={(e) => e.stopPropagation()}
			>
				<Tooltip>
					<TooltipTrigger asChild>
						<GripVertical className="h-5 w-5" />
					</TooltipTrigger>
					<TooltipContent side="left">
						Click and drag to move task
					</TooltipContent>
				</Tooltip>
			</div>
			<div
				className="absolute bottom-3 right-3 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity dark:bg-transparent dark:from-black-50
dark:to-black pl-4 mt-0.5"
				onClick={(e) => e.stopPropagation()}
			>
				<Tooltip>
					<TooltipTrigger asChild>
						<button
							type="button"
							onClick={() => onDelete(task)}
							className={cn(
								"text-[11px] font-medium relative text-white bg-secondary px-1.5 py-0.5 rounded transition-all duration-150 gap-1 flex items-center justify-center z-10",
								confirming
									? "opacity-100 translate-x-5"
									: "opacity-0 translate-x-2 pointer-events-none",
							)}
						>
							<CheckCircle2 className="h-3 w-3" />
							Confirm
						</button>
					</TooltipTrigger>
					<TooltipContent side="top">
						This will permanently delete the task
					</TooltipContent>
				</Tooltip>
				<Tooltip>
					<TooltipTrigger asChild>
						<button
							type="button"
							onClick={() => setConfirming((prev) => !prev)}
							className={cn(
								"p-0.5 rounded transition-colors",
								confirming
									? "text-danger"
									: "text-muted hover:text-danger",
							)}
						>
							<Trash2 className="h-3.5 w-3.5" />
						</button>
					</TooltipTrigger>
					<TooltipContent side="left">
						Click to delete task
					</TooltipContent>
				</Tooltip>
			</div>
		</>
	);
});

// ── DraggableTaskCard ─────────────────────────────────────────────────────────

// Drag only moves a card between stages; there is no in-stage reordering, so
// cards are plain draggables and only the stage cells are droppables.
function DraggableTaskCardBase({
	task,
	projectsById,
	onDelete,
	onView,
}: {
	task: UiTask;
	projectsById: Map<string, Project>;
	onDelete: (task: UiTask) => void;
	onView: (task: UiTask) => void;
}) {
	const [confirming, setConfirming] = useState(false);
	const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
		id: task.id,
	});

	return (
		<div
			ref={setNodeRef}
			style={isDragging ? { opacity: 0.35 } : undefined}
			className="relative group cursor-pointer rounded-lg overflow-hidden"
			onMouseLeave={() => setConfirming(false)}
			onClick={() => onView(task)}
		>
			<div className="absolute w-3/4 bg-linear-to-r from-white-50 dark:from-black-50 to-white dark:to-black transition-all divide-gray-200 h-full right-0 opacity-0 group-hover:opacity-100" />
			<CardActions
				task={task}
				confirming={confirming}
				setConfirming={setConfirming}
				onDelete={onDelete}
				attributes={attributes}
				listeners={listeners}
			/>
			<TaskCardContent task={task} projectsById={projectsById} />
		</div>
	);
}

export const DraggableTaskCard = memo(DraggableTaskCardBase);

export type { ColumnId, UiTask };
export { COLUMN_IDS };
